import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

// FAQ as provided by the client (Oct 2026). English is the official text;
// Tamil is a translation for the language toggle. Pairs are [english, tamil].
const faqs = [
  {
    q: ["How does the NOC approval process work?", "தடையின்மைச் சான்று (NOC) ஒப்புதல் நடைமுறை எவ்வாறு செயல்படுகிறது?"],
    a: [
      "Applicants must register on the ERI Portal, select the water body proposed for restoration, provide the required details and submit the application online. The application will be scrutinised by the concerned departmental officers at Level 1 (L1) and approval by the Level 2 (L2), the No Objection Certificate (NOC) will be made available for download through the portal.",
      "விண்ணப்பதாரர்கள் ஏரி இணையதளத்தில் பதிவு செய்து, புனரமைக்க முன்மொழியப்படும் நீர்நிலையைத் தேர்வு செய்து, தேவையான விவரங்களை வழங்கி, இணையவழியில் விண்ணப்பத்தைச் சமர்ப்பிக்க வேண்டும். விண்ணப்பம் சம்பந்தப்பட்ட துறை அலுவலர்களால் நிலை 1 (L1)-இல் ஆய்வு செய்யப்பட்டு, நிலை 2 (L2) ஒப்புதலுக்குப் பின், தடையின்மைச் சான்று (NOC) இணையதளத்தில் பதிவிறக்கம் செய்யக் கிடைக்கும்.",
    ],
  },
  {
    q: ["Is there any fee for submitting NOC applications?", "தடையின்மைச் சான்று விண்ணப்பத்திற்குக் கட்டணம் உண்டா?"],
    a: [
      "No application fee is proposed for submitting NOC applications through the ERI Portal. This is subject to confirmation of the applicable Government instructions.",
      "ஏரி இணையதளத்தின் மூலம் தடையின்மைச் சான்று விண்ணப்பம் சமர்ப்பிக்க விண்ணப்பக் கட்டணம் எதுவும் முன்மொழியப்படவில்லை. இது பொருந்தக்கூடிய அரசு அறிவுறுத்தல்களின் உறுதிப்படுத்தலுக்கு உட்பட்டது.",
    ],
  },
  {
    q: ["What happens if my application is returned or rejected?", "என் விண்ணப்பம் திருப்பி அனுப்பப்பட்டாலோ நிராகரிக்கப்பட்டாலோ என்ன ஆகும்?"],
    a: [
      "If an application is returned for clarification or correction, the applicant may review the remarks, provide the required information and resubmit the application. If rejected, the applicant can view the reasons recorded by the concerned authority in the portal.",
      "விளக்கம் அல்லது திருத்தத்திற்காக விண்ணப்பம் திருப்பி அனுப்பப்பட்டால், விண்ணப்பதாரர் குறிப்புரைகளைப் பார்வையிட்டு, தேவையான தகவல்களை வழங்கி, விண்ணப்பத்தை மீண்டும் சமர்ப்பிக்கலாம். நிராகரிக்கப்பட்டால், சம்பந்தப்பட்ட அலுவலர் பதிவு செய்த காரணங்களை இணையதளத்தில் காணலாம்.",
    ],
  },
  {
    q: ["Can I apply for multiple water bodies in different districts?", "வெவ்வேறு மாவட்டங்களில் உள்ள பல நீர்நிலைகளுக்கு விண்ணப்பிக்கலாமா?"],
    a: [
      "Yes. Registered applicants may submit separate applications for multiple water bodies located in different districts of Tamil Nadu. Each application will be processed by the respective jurisdictional department and competent authority.",
      "ஆம். பதிவு செய்த விண்ணப்பதாரர்கள் தமிழ்நாட்டின் வெவ்வேறு மாவட்டங்களில் உள்ள பல நீர்நிலைகளுக்குத் தனித்தனி விண்ணப்பங்களைச் சமர்ப்பிக்கலாம். ஒவ்வொரு விண்ணப்பமும் அதற்குரிய அதிகார வரம்பிலுள்ள துறை மற்றும் தகுதிவாய்ந்த அலுவலரால் செயல்படுத்தப்படும்.",
    ],
  },
  {
    q: ["Who can apply for an NOC through the ERI Portal?", "ஏரி இணையதளத்தின் மூலம் தடையின்மைச் சான்றுக்கு யார் விண்ணப்பிக்கலாம்?"],
    a: [
      "NGOs, voluntary organisations, corporate entities participating in CSR activities and eligible individuals interested in restoring water bodies may apply, subject to the eligibility requirements prescribed by the concerned department.",
      "தன்னார்வத் தொண்டு நிறுவனங்கள், தன்னார்வ அமைப்புகள், பெருநிறுவன சமூகப் பொறுப்பு (CSR) நடவடிக்கைகளில் பங்கேற்கும் நிறுவனங்கள் மற்றும் நீர்நிலைகளைப் புனரமைப்பதில் ஆர்வமுள்ள தகுதியான தனிநபர்கள், சம்பந்தப்பட்ட துறை வகுத்துள்ள தகுதி நிபந்தனைகளுக்கு உட்பட்டு விண்ணப்பிக்கலாம்.",
    ],
  },
  {
    q: ["How can I register on the ERI Portal?", "ஏரி இணையதளத்தில் எவ்வாறு பதிவு செய்வது?"],
    a: [
      "Click \"Register Now for NOC\" on the homepage, select the appropriate applicant type, fill in the required registration details and complete the registration process. Once registered, the applicant can log in and submit applications.",
      "முகப்புப் பக்கத்தில் \"தடையின்மைச் சான்றுக்கு இப்போதே பதிவு செய்க\" என்பதைக் கிளிக் செய்து, பொருத்தமான விண்ணப்பதாரர் வகையைத் தேர்வு செய்து, தேவையான பதிவு விவரங்களை நிரப்பி, பதிவை நிறைவு செய்யவும். பதிவு செய்த பின், விண்ணப்பதாரர் உள்நுழைந்து விண்ணப்பங்களைச் சமர்ப்பிக்கலாம்.",
    ],
  },
  {
    q: ["How can I identify and select a water body for restoration?", "புனரமைப்பிற்கான நீர்நிலையை எவ்வாறு கண்டறிந்து தேர்வு செய்வது?"],
    a: [
      "After logging in, select the Department, district, taluk and other relevant location details to view the available water bodies. Select the proposed water body and proceed with the NOC application.",
      "உள்நுழைந்த பின், துறை, மாவட்டம், வட்டம் மற்றும் பிற இருப்பிட விவரங்களைத் தேர்வு செய்து கிடைக்கும் நீர்நிலைகளைக் காணலாம். முன்மொழியப்படும் நீர்நிலையைத் தேர்வு செய்து தடையின்மைச் சான்று விண்ணப்பத்தைத் தொடரவும்.",
    ],
  },
  {
    q: ["Can I apply for a water body where restoration works have already been carried out?", "ஏற்கனவே புனரமைப்புப் பணிகள் மேற்கொள்ளப்பட்ட நீர்நிலைக்கு விண்ணப்பிக்கலாமா?"],
    a: [
      "Yes. Applicants may view the details of restoration works already carried out in the selected water body and submit proposals for additional works, subject to technical feasibility and approval by the competent authority.",
      "ஆம். தேர்வு செய்த நீர்நிலையில் ஏற்கனவே மேற்கொள்ளப்பட்ட புனரமைப்புப் பணிகளின் விவரங்களைப் பார்வையிட்டு, தொழில்நுட்பச் சாத்தியக்கூறு மற்றும் தகுதிவாய்ந்த அலுவலரின் ஒப்புதலுக்கு உட்பட்டு, கூடுதல் பணிகளுக்கான முன்மொழிவுகளைச் சமர்ப்பிக்கலாம்.",
    ],
  },
  {
    q: ["How can I track the status of my NOC application?", "என் தடையின்மைச் சான்று விண்ணப்பத்தின் நிலையை எவ்வாறு கண்காணிப்பது?"],
    a: [
      "Applicants can log in to the ERI Portal and view the current status of their application, including its approval stage, returned applications and final decision.",
      "விண்ணப்பதாரர்கள் ஏரி இணையதளத்தில் உள்நுழைந்து, ஒப்புதல் நிலை, திருப்பி அனுப்பப்பட்ட விண்ணப்பங்கள் மற்றும் இறுதி முடிவு உள்ளிட்ட தங்கள் விண்ணப்பத்தின் தற்போதைய நிலையைக் காணலாம்.",
    ],
  },
  {
    q: ["How can I download the approved NOC?", "ஒப்புதல் அளிக்கப்பட்ட தடையின்மைச் சான்றை எவ்வாறு பதிவிறக்கம் செய்வது?"],
    a: [
      "Once the application is approved by the competent authority, the applicant can log in to the portal and download the NOC from the relevant application.",
      "தகுதிவாய்ந்த அலுவலர் விண்ணப்பத்திற்கு ஒப்புதல் அளித்தவுடன், விண்ணப்பதாரர் இணையதளத்தில் உள்நுழைந்து அந்த விண்ணப்பத்திலிருந்து தடையின்மைச் சான்றைப் பதிவிறக்கம் செய்யலாம்.",
    ],
  },
  {
    q: ["Can restoration works be started immediately after submitting an application?", "விண்ணப்பம் சமர்ப்பித்தவுடன் புனரமைப்புப் பணிகளைத் தொடங்கலாமா?"],
    a: [
      "No. Restoration works should commence only after obtaining the required NOC and complying with the conditions specified by the competent authority.",
      "இல்லை. தேவையான தடையின்மைச் சான்றைப் பெற்று, தகுதிவாய்ந்த அலுவலர் குறிப்பிட்ட நிபந்தனைகளைப் பின்பற்றிய பின்னரே புனரமைப்புப் பணிகளைத் தொடங்க வேண்டும்.",
    ],
  },
  {
    q: ["How can I obtain a Work Completion Certificate?", "பணி நிறைவுச் சான்றை எவ்வாறு பெறுவது?"],
    a: [
      "After completing the approved restoration works, the applicant must upload the required completion details and photographs. The concerned departmental officers will verify the completed works. Following the prescribed L2 approval, the Work Completion Certificate will be made available through the portal.",
      "ஒப்புதல் அளிக்கப்பட்ட புனரமைப்புப் பணிகளை முடித்த பின், விண்ணப்பதாரர் தேவையான நிறைவு விவரங்களையும் புகைப்படங்களையும் பதிவேற்ற வேண்டும். சம்பந்தப்பட்ட துறை அலுவலர்கள் முடிக்கப்பட்ட பணிகளைச் சரிபார்ப்பார்கள். நிலை 2 (L2) ஒப்புதலுக்குப் பின், பணி நிறைவுச் சான்று இணையதளத்தில் கிடைக்கும்.",
    ],
  },
  {
    q: ["What types of restoration works can be proposed?", "எந்த வகையான புனரமைப்புப் பணிகளை முன்மொழியலாம்?"],
    a: [
      "Applicants may propose desilting, removal of vegetation, bund strengthening, restoration of water-carrying channels and other water body improvement activities, subject to departmental guidelines and technical approval.",
      "துறை வழிகாட்டுதல்கள் மற்றும் தொழில்நுட்ப ஒப்புதலுக்கு உட்பட்டு, தூர்வாருதல், தாவரங்களை அகற்றுதல், கரையை வலுப்படுத்துதல், நீர்வரத்துக் கால்வாய்களைச் சீரமைத்தல் மற்றும் பிற நீர்நிலை மேம்பாட்டுப் பணிகளை விண்ணப்பதாரர்கள் முன்மொழியலாம்.",
    ],
  },
  {
    q: ["Is it mandatory to upload photographs of restoration works?", "புனரமைப்புப் பணிகளின் புகைப்படங்களைப் பதிவேற்றுவது கட்டாயமா?"],
    a: [
      "Yes. Applicants must upload photographs at the prescribed stages, including before commencement, during execution and after completion of the works, as required by the portal.",
      "ஆம். பணி தொடங்குவதற்கு முன், பணி நடைபெறும் போது மற்றும் பணி முடிந்த பின் என இணையதளம் கோரும் நிலைகளில் விண்ணப்பதாரர்கள் புகைப்படங்களைப் பதிவேற்ற வேண்டும்.",
    ],
  },
  {
    q: ["What is the difference between an Organisation and an Individual applicant?", "அமைப்பு மற்றும் தனிநபர் விண்ணப்பதாரருக்கு இடையிலான வேறுபாடு என்ன?"],
    a: [
      "An Organisation applicant represents an NGO, trust, association, company or other eligible institution. An Individual applicant applies in their personal capacity. The registration details and supporting documents may vary according to the applicant type.",
      "அமைப்பு விண்ணப்பதாரர் என்பவர் தன்னார்வத் தொண்டு நிறுவனம், அறக்கட்டளை, சங்கம், நிறுவனம் அல்லது பிற தகுதியான அமைப்பைப் பிரதிநிதித்துவப்படுத்துபவர். தனிநபர் விண்ணப்பதாரர் தனது சொந்தத் தகுதியில் விண்ணப்பிப்பவர். விண்ணப்பதாரர் வகைக்கு ஏற்ப பதிவு விவரங்களும் ஆதார ஆவணங்களும் மாறுபடலாம்.",
    ],
  },
];

const FAQPage = () => {
  const { tr } = useLang();
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const next = new Set(openItems);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setOpenItems(next);
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-20">
      {/* Page header */}
      <div className="relative bg-cover bg-center border-b-4 border-brand-accent" style={{ backgroundImage: "url('/images/water3.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-700/95 via-brand-600/85 to-teal-500/70" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <h1 className="text-3xl lg:text-4xl font-semibold text-white">
            {tr('Frequently Asked Questions', 'அடிக்கடி கேட்கப்படும் கேள்விகள்')}
          </h1>
          <p className="mt-2 text-white/90 max-w-2xl">
            {tr(
              'Find answers to common questions about the application process and requirements.',
              'விண்ணப்ப நடைமுறை மற்றும் தேவைகள் குறித்த பொதுவான கேள்விகளுக்கான பதில்கள்.'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <div className="bg-white border border-slate-200 divide-y divide-slate-200">
          {faqs.map((faq, index) => (
            <div key={faq.q[0]}>
              <button
                type="button"
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-4 text-left hover:bg-slate-50 flex items-center justify-between gap-6"
              >
                <h3 className="text-base lg:text-lg font-medium text-slate-800"><span className="text-brand-700 font-semibold mr-2">Q{index + 1}.</span>{tr(...faq.q)}</h3>
                {openItems.has(index)
                  ? <Minus className="w-5 h-5 text-brand-700 flex-shrink-0" />
                  : <Plus className="w-5 h-5 text-brand-700 flex-shrink-0" />}
              </button>
              {openItems.has(index) && (
                <div className="px-6 pb-5 border-l-4 border-brand-accent ml-6 mr-6 mb-4 bg-brand-50">
                  <p className="text-slate-700 leading-relaxed pt-3">{tr(...faq.a)}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white border border-slate-200 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{tr('Need Additional Help?', 'மேலும் உதவி தேவையா?')}</h3>
            <p className="text-sm text-slate-600">
              {tr(
                'If your question is not covered here, please contact the concerned department.',
                'உங்கள் கேள்விக்கு இங்கு பதில் இல்லையெனில், சம்பந்தப்பட்ட துறையைத் தொடர்பு கொள்ளவும்.'
              )}
            </p>
          </div>
          <Link to="/contact" className="px-5 py-2.5 text-sm font-semibold bg-brand-700 text-white hover:bg-brand-800 text-center">
            {tr('Contact Departments', 'துறைகளைத் தொடர்பு கொள்ள')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
